import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import { UserStorageService } from './user-storage.service';

/**
 * Manages the QA backdoor token that gates the test:email@test.com login
 * (see server.ts's `authenticate` decorator). Stored as a device-level
 * (non-user-scoped) localStorage value since it's needed before any user is
 * authenticated -- it's what unlocks the ability to log in at all via the
 * test-login tab.
 */
@Injectable({ providedIn: 'root' })
export class QaBackdoorService {
  private http = inject(HttpClient);
  private userStorage = inject(UserStorageService);
  private logger = inject(LoggerService).getLogger('qa-backdoor');

  private readonly STORAGE_KEY = 'qa_backdoor_token';

  getToken(): string | null {
    return this.userStorage.getGlobalItem(this.STORAGE_KEY);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  clear(): void {
    this.userStorage.removeGlobalItem(this.STORAGE_KEY);
  }

  /** Validates the candidate token against the backend; saves it only if valid. */
  async validateAndSave(candidateToken: string): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        this.http.post<{ valid: boolean }>(
          `${environment.apiUrl}/qa/validate-backdoor-token`,
          {},
          { headers: { 'x-qa-backdoor-token': candidateToken } },
        ),
      );

      if (result.valid) {
        this.userStorage.setGlobalItem(this.STORAGE_KEY, candidateToken);
        return true;
      }
      return false;
    } catch (err) {
      this.logger.error('Failed to validate QA backdoor token:', err);
      return false;
    }
  }
}
